import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import {
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  WithStyles,
  Box,
  Card,
  CardContent,
} from '@material-ui/core';

import { Link } from 'react-router-dom';
import { Rating } from '@material-ui/lab';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer';
import styles from './styles';
import { CustomerVisits } from '../../redux/types/customers';
import { formatToESTDate } from '../../utils/date';

type Props = {
  activity: CustomerVisits;
} & WithStyles<typeof styles>;

const ActivityFeedItem: React.FC<Props> = (props) => {
  const { classes, activity } = props;
  const propertyAgents = activity.agents.map((agent) => {
    const prefix = agent.is_project_manager ? 'PM' : 'AG';

    return `${prefix}: ${agent.firstname} ${agent.lastname}`;
  });
  const propertyAddress = activity.address_1;
  const [formatedDate, time] = formatToESTDate(activity.modified_on, activity.propertyTimezone, activity.propTimezoneValue).split(',');
  const [feedbackLines, setFeedbackLines] = useState<string[]>([]);
  const [ratingLine, setRatingLine] = useState<number>();
  const [postTourSurveys, setPostTourSurveys] = useState<string[]>([]);


  useEffect(() => {
    const lines = activity.remarks?.split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0 && line !== 'User sent feedback:');

    if (!lines?.length) {
      return;
    }

    const ratingIndex = lines.findIndex((line) => line.toLowerCase().includes('stars'));

    if (ratingIndex !== -1) {
      setRatingLine(parseFloat(lines[ratingIndex]));
    }

    const commentsIndex = lines.findIndex((line) => line.toLowerCase().startsWith('comments:'));
    const postTourIndex = lines.findIndex((line) => line.toLowerCase().startsWith('post tour survey'));

    let linesOfFeedback: string[] = [];

    if (commentsIndex >= 0) {
      if (postTourIndex > commentsIndex) {
        linesOfFeedback = lines.slice(commentsIndex + 1, postTourIndex);
      } else {
        linesOfFeedback = lines.slice(commentsIndex + 1);
      }
    }

    setFeedbackLines(linesOfFeedback);

    if (postTourIndex !== -1) {
      const surveyLines = lines.slice(postTourIndex + 1);
      setPostTourSurveys(surveyLines);
    }
  }, [activity.remarks]);

  return (
    <div className={classes.container}>
      <div className={classes.circle} />
      <div className={classnames(classes.drawLineStyles, classes.content)}>
        <Grid container>
          <Grid item xs={12} sm={2}>
            <Typography className={classes.activityDate}>
              {formatedDate}
            </Typography>
            <Typography className={classes.activityDate}>
              {time}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={1}>
            <Typography color="textSecondary">Visit status</Typography>
            <Typography>{activity.visit_status_id_value}</Typography>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography color="textSecondary">Attribution</Typography>
            <Typography>{activity.attribution}</Typography>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography color="textSecondary">Address</Typography>
            <Link to={`/properties/${activity.property_id}`}>
              <Typography>{propertyAddress}</Typography>
            </Link>
          </Grid>
          <Grid item xs={12} sm={1}>
            <Typography color="textSecondary">Lot #</Typography>
            <Typography>{activity.house_num}</Typography>
          </Grid>
          <Grid item xs={12} sm={1}>
            <Typography color="textSecondary">Client</Typography>
            <Typography>{activity.builder}</Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography color="textSecondary">Agent</Typography>
            <Typography>{propertyAgents.join(', ')}</Typography>
          </Grid>
        </Grid>
        {activity.remarks?.includes('User sent feedback') ? (
          <Box mt={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6">
                  User Feedback
                </Typography>

                {!!ratingLine && (
                  <Box className={classes.rating}>
                    <Typography variant="body1">
                      Rating:
                    </Typography>
                    <Rating value={ratingLine} readOnly />
                  </Box>
                )}

                {!!feedbackLines.length && (
                  <>
                    <Typography variant="subtitle1">
                      Comments:
                    </Typography>
                    <List dense>
                      {feedbackLines.map((line) => {
                        const isLiked = line.toLowerCase().includes('loved:');
                        return (
                          <ListItem key={line}>
                            <ListItemIcon>
                              {isLiked
                                ? <ThumbUpIcon />
                                : <ThumbDownIcon color="error" />
                              }
                            </ListItemIcon>
                            <ListItemText primary={line} />
                          </ListItem>
                        );
                      })}
                    </List>
                  </>
                )}

                {!!postTourSurveys.length && (
                  <>
                    <Divider className={classes.divider} />
                    <Typography variant="subtitle1">
                      Post Tour Surveys:
                    </Typography>
                    <List dense>
                      {postTourSurveys.map((line) => (
                        <ListItem key={line}>
                          <ListItemIcon>
                            <QuestionAnswerIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={line} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        ) : (
          <Typography className={classes.comment}>{activity.remarks}</Typography>
        )}
      </div>
      <div className={classnames(classes.drawLineStyles, classes.line)} />
    </div>
  );
};

export default React.memo(ActivityFeedItem);
